import { BriefcaseIcon, CheckIcon, CircularLoaderIcon, EmailIcon, IdCardIcon, RocketIcon, UserIcon } from "@/components/icons";
import { ArrowLeft } from "lucide-react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { useSnapshot } from "valtio";
import { registrationStore } from "@/features/auth/store";
import { useEffect } from "react";
import { EMAIL_VERIFICATION_ENABLED } from "@/features/auth/registration";

export type Steps = {
    title: string,
    description: string
    icon: React.ReactNode
    path: string
    isComplete?: boolean
}

const Register = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const store = useSnapshot(registrationStore);

    const steps: Steps[] = [
        {
            icon: <UserIcon className="size-6" />,
            title: "Personal Details",
            description: "Provide your personal details",
            path: "/auth/register/personal",
            isComplete: store.personal != undefined
        },
        {
            icon: <IdCardIcon className="size-6" />,
            title: "Identification Details",
            description: "Provide your identification details",
            path: "/auth/register/identity",
            isComplete: store.identification != undefined
        },
        {
            icon: <BriefcaseIcon className="size-6" />,
            title: "Business Details",
            description: "Provide your business details",
            path: "/auth/register/business",
            isComplete: store.business != undefined
        },

        {
            icon: <RocketIcon className="size-6" />,
            title: "Welcome to ZCT",
            description: "Get up and running in 1 minute",
            path: "/auth/register/welcome",
            isComplete: false
        },
    ]

    // The "Verify Your Email" step is omitted while email delivery is unavailable; the
    // business step submits the registration directly in that case.
    if (EMAIL_VERIFICATION_ENABLED) {
        steps.splice(steps.length - 1, 0, {
            icon: <EmailIcon className="size-6" />,
            title: "Verify Your Email",
            description: "Verify Your Email",
            path: "/auth/register/verification",
            isComplete: store.token != undefined
        })
    }

    useEffect(() => {
        if(store.personal == undefined) {
            navigate("/auth/register/personal")
        }
    }, [store])

    return (
        <div className="flex flex-wrap md:flex-nowrap min-h-screen">
            <section className="flex flex-col justify-between w-full md:w-1/4 min-h-full bg-[#F5F2F0]">
                <div className="p-6 md:p-8 space-y-12">
                    <h4 className="text-secondary text-lg x:text-xl font-semibold">
                        New Account Registration
                    </h4>

                    <div className="space-y-2">
                        {
                            steps.map((el, index) => {
                                const isActive = el.path == location.pathname;
                                return (
                                    <div 
                                        className={twMerge(
                                            "relative flex items-start space-x-4",
                                            isActive ? "text-secondary" : "text-[#9E8F82]" 
                                        )} 
                                        key={el.path}
                                    >
                                        <div className="flex flex-col items-center space-y-2">
                                            <div className={twMerge(
                                                "flex justify-center items-center p-2 rounded-lg",
                                                "border border-secondary-100 bg-white",
                                                isActive ? "text-primary" : "text-[#9E8F82]" 
                                            )}>
                                                {el.icon}
                                            </div>
                                            <div 
                                                className={twMerge(
                                                    "w-0.5 h-10 rounded",
                                                    index == steps.length - 1 && "hidden",
                                                    isActive ? "bg-primary" : "bg-[#9E8F82]"
                                                )}
                                            ></div>
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="text-sm md:text-bases font-medium mb-1">
                                                {el.title}
                                            </h4>
                                            <p className="text-xs">
                                                {el.description}
                                            </p>
                                        </div>
                                        

                                        <div className="ml-auto w-auto">
                                            {
                                                isActive && el.isComplete == false && (
                                                    <CircularLoaderIcon className="size-5 text-primary" />
                                                )
                                            }

                                            {
                                                el.isComplete && (
                                                    <CheckIcon className="size-5 text-primary" />
                                                )
                                            }
                                        </div>
                                    </div>
                                )
                            })
                        }
                    </div>
                </div>

                <Link 
                    to="/auth/login" 
                    className="flex items-center space-x-2 m-4 text-sm text-secondary hover:underline"
                >
                    <ArrowLeft className="size-4" strokeWidth={3} />
                    <span>Back to Login</span>
                </Link>
            </section>

            <section className="w-full md:w-3/4 flex flex-col justify-center">
                <div className="w-full md:w-3/5 xl:w-1/2 mx-auto">
                    <Outlet />
                </div>
            </section>
        </div>
    )
}

export default Register